import type { Theme, ThemeName } from './theme.types';

/**
 * Configurazione di tutti i temi disponibili
 * 
 * Ogni tema può essere completamente personalizzato modificando questo file.
 * I colori seguono la convenzione DaisyUI per compatibilità e familiarità.
 */

/**
 * Tema Light (Default)
 */
const light: Theme = {
  name: 'light',
  displayName: '☀️ Light',
  colors: {
    primary: '#3b82f6',           // blue-500
    primaryContent: '#ffffff',
    primaryFocus: '#2563eb',      // blue-600
    
    secondary: '#6366f1',         // indigo-500
    secondaryContent: '#ffffff',
    secondaryFocus: '#4f46e5',    // indigo-600
    
    accent: '#f59e0b',            // amber-500
    accentContent: '#ffffff',
    accentFocus: '#d97706',       // amber-600
    
    neutral: '#3d4451',           // gray-700
    neutralContent: '#ffffff',
    neutralFocus: '#2a2e37',      // gray-800
    
    base100: '#ffffff',
    base200: '#f3f4f6',           // gray-100
    base300: '#e5e7eb',           // gray-200
    baseContent: '#1f2937',       // gray-800
    
    info: '#0ea5e9',              // sky-500
    infoContent: '#ffffff',
    success: '#10b981',           // green-500
    successContent: '#ffffff',
    warning: '#f59e0b',           // amber-500
    warningContent: '#ffffff',
    error: '#ef4444',             // red-500
    errorContent: '#ffffff',
  },
  borderRadius: {
    base: '0.5rem',
    box: '1rem',
    btn: '0.5rem',
    badge: '9999px',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: true,
    animated: true,
  },
};

/**
 * Tema Dark
 */
const dark: Theme = {
  name: 'dark',
  displayName: '🌙 Dark',
  colors: {
    primary: '#60a5fa',           // blue-400
    primaryContent: '#1e293b',    // slate-800
    primaryFocus: '#3b82f6',      // blue-500
    
    secondary: '#818cf8',         // indigo-400
    secondaryContent: '#1e293b',
    secondaryFocus: '#6366f1',    // indigo-500
    
    accent: '#fbbf24',            // amber-400
    accentContent: '#1e293b',
    accentFocus: '#f59e0b',       // amber-500
    
    neutral: '#2a323c',
    neutralContent: '#e5e7eb',
    neutralFocus: '#1f2937',
    
    base100: '#1e293b',           // slate-800
    base200: '#1a2332',
    base300: '#151c2a',
    baseContent: '#e5e7eb',       // gray-200
    
    info: '#38bdf8',              // sky-400
    infoContent: '#1e293b',
    success: '#34d399',           // green-400
    successContent: '#1e293b',
    warning: '#fbbf24',           // amber-400
    warningContent: '#1e293b',
    error: '#f87171',             // red-400
    errorContent: '#1e293b',
  },
  borderRadius: {
    base: '0.5rem',
    box: '1rem',
    btn: '0.5rem',
    badge: '9999px',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: true,
    animated: true,
  },
};

/**
 * Tema Cupcake (Pastello)
 */
const cupcake: Theme = {
  name: 'cupcake',
  displayName: '🧁 Cupcake',
  colors: {
    primary: '#ec4899',           // pink-500
    primaryContent: '#ffffff',
    primaryFocus: '#db2777',      // pink-600
    
    secondary: '#a78bfa',         // violet-400
    secondaryContent: '#ffffff',
    secondaryFocus: '#8b5cf6',    // violet-500
    
    accent: '#fbbf24',            // amber-400
    accentContent: '#1e293b',
    accentFocus: '#f59e0b',
    
    neutral: '#291334',
    neutralContent: '#fef3c7',
    neutralFocus: '#1e0a28',
    
    base100: '#faf7f5',
    base200: '#efeae6',
    base300: '#e7e2df',
    baseContent: '#291334',
    
    info: '#7dd3fc',              // sky-300
    infoContent: '#1e293b',
    success: '#86efac',           // green-300
    successContent: '#1e293b',
    warning: '#fde047',           // yellow-300
    warningContent: '#1e293b',
    error: '#fca5a5',             // red-300
    errorContent: '#1e293b',
  },
  borderRadius: {
    base: '1rem',
    box: '1.5rem',
    btn: '9999px',
    badge: '9999px',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: true,
    animated: true,
  },
};

/**
 * Tema Corporate (Business)
 */
const corporate: Theme = {
  name: 'corporate',
  displayName: '💼 Corporate',
  colors: {
    primary: '#0369a1',           // sky-700
    primaryContent: '#ffffff',
    primaryFocus: '#075985',      // sky-800
    
    secondary: '#64748b',         // slate-500
    secondaryContent: '#ffffff',
    secondaryFocus: '#475569',    // slate-600
    
    accent: '#0891b2',            // cyan-600
    accentContent: '#ffffff',
    accentFocus: '#0e7490',       // cyan-700
    
    neutral: '#1e293b',           // slate-800
    neutralContent: '#f1f5f9',
    neutralFocus: '#0f172a',      // slate-900
    
    base100: '#ffffff',
    base200: '#f8fafc',           // slate-50
    base300: '#f1f5f9',           // slate-100
    baseContent: '#1e293b',
    
    info: '#0ea5e9',
    infoContent: '#ffffff',
    success: '#059669',           // green-600
    successContent: '#ffffff',
    warning: '#d97706',           // amber-600
    warningContent: '#ffffff',
    error: '#dc2626',             // red-600
    errorContent: '#ffffff',
  },
  borderRadius: {
    base: '0.25rem',
    box: '0.5rem',
    btn: '0.25rem',
    badge: '0.25rem',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: false,
    animated: false,
  },
};

/**
 * Tema Synthwave (Neon)
 */
const synthwave: Theme = {
  name: 'synthwave',
  displayName: '🌆 Synthwave',
  colors: {
    primary: '#e779c1',
    primaryContent: '#1a103d',
    primaryFocus: '#d946a6',
    
    secondary: '#58c7f3',
    secondaryContent: '#1a103d',
    secondaryFocus: '#3bb8e8',
    
    accent: '#f9c825',
    accentContent: '#1a103d',
    accentFocus: '#e5b31a',
    
    neutral: '#1e1638',
    neutralContent: '#f1e8ff',
    neutralFocus: '#12102a',
    
    base100: '#1a103d',
    base200: '#150c33',
    base300: '#0f0829',
    baseContent: '#f1e8ff',
    
    info: '#58c7f3',
    infoContent: '#1a103d',
    success: '#71efca',
    successContent: '#1a103d',
    warning: '#f9c825',
    warningContent: '#1a103d',
    error: '#ff6b9d',
    errorContent: '#1a103d',
  },
  borderRadius: {
    base: '0',
    box: '0',
    btn: '0',
    badge: '0',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: false,
    animated: true,
  },
};

/**
 * Tema Retro (Vintage)
 */
const retro: Theme = {
  name: 'retro',
  displayName: '🕹️ Retro',
  colors: {
    primary: '#ef9995',
    primaryContent: '#282425',
    primaryFocus: '#e67c78',
    
    secondary: '#a4cbb4',
    secondaryContent: '#282425',
    secondaryFocus: '#8bb99f',
    
    accent: '#e3c9b1',
    accentContent: '#282425',
    accentFocus: '#d9b89c',
    
    neutral: '#2e2b2c',
    neutralContent: '#f5e9d8',
    neutralFocus: '#1f1c1d',
    
    base100: '#f5e9d8',
    base200: '#ede4d3',
    base300: '#e4d8c5',
    baseContent: '#282425',
    
    info: '#6cb4c3',
    infoContent: '#282425',
    success: '#a4cbb4',
    successContent: '#282425',
    warning: '#f7c266',
    warningContent: '#282425',
    error: '#e36b6b',
    errorContent: '#282425',
  },
  borderRadius: {
    base: '0',
    box: '0',
    btn: '0',
    badge: '0',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: false,
    animated: false,
  },
};

/**
 * Tema Cyberpunk (Futuristico)
 */
const cyberpunk: Theme = {
  name: 'cyberpunk',
  displayName: '🤖 Cyberpunk',
  colors: {
    primary: '#ffee00',
    primaryContent: '#120a00',
    primaryFocus: '#e6d500',
    
    secondary: '#00ffff',
    secondaryContent: '#001212',
    secondaryFocus: '#00e6e6',
    
    accent: '#ff00ff',
    accentContent: '#120012',
    accentFocus: '#e600e6',
    
    neutral: '#16181d',
    neutralContent: '#f0f0f0',
    neutralFocus: '#0d0e11',
    
    base100: '#1c1c1c',
    base200: '#151515',
    base300: '#0e0e0e',
    baseContent: '#f0f0f0',
    
    info: '#00d9ff',
    infoContent: '#001419',
    success: '#00ff9f',
    successContent: '#001912',
    warning: '#ff9900',
    warningContent: '#191200',
    error: '#ff0055',
    errorContent: '#190008',
  },
  borderRadius: {
    base: '0',
    box: '0',
    btn: '0',
    badge: '0',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: false,
    animated: true,
  },
};

/**
 * Tema Valentine (Romantico)
 */
const valentine: Theme = {
  name: 'valentine',
  displayName: '💝 Valentine',
  colors: {
    primary: '#e96d7b',
    primaryContent: '#ffffff',
    primaryFocus: '#d95766',
    
    secondary: '#a991f7',
    secondaryContent: '#ffffff',
    secondaryFocus: '#9378e8',
    
    accent: '#88dbdd',
    accentContent: '#1a2633',
    accentFocus: '#73c5c7',
    
    neutral: '#632c3b',
    neutralContent: '#f8dce6',
    neutralFocus: '#4d222d',
    
    base100: '#fef3f7',
    base200: '#fce7ef',
    base300: '#fadbe5',
    baseContent: '#632c3b',
    
    info: '#7dd3fc',
    infoContent: '#1a2633',
    success: '#86efac',
    successContent: '#1a2633',
    warning: '#fde047',
    warningContent: '#1a2633',
    error: '#fca5a5',
    errorContent: '#1a2633',
  },
  borderRadius: {
    base: '1rem',
    box: '1.5rem',
    btn: '9999px',
    badge: '9999px',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: true,
    animated: true,
  },
};

/**
 * Tema Aqua (Acquatico)
 */
const aqua: Theme = {
  name: 'aqua',
  displayName: '🌊 Aqua',
  colors: {
    primary: '#06b6d4',           // cyan-500
    primaryContent: '#ffffff',
    primaryFocus: '#0891b2',      // cyan-600
    
    secondary: '#0ea5e9',         // sky-500
    secondaryContent: '#ffffff',
    secondaryFocus: '#0284c7',    // sky-600
    
    accent: '#14b8a6',            // teal-500
    accentContent: '#ffffff',
    accentFocus: '#0d9488',       // teal-600
    
    neutral: '#164e63',           // cyan-900
    neutralContent: '#ecfeff',
    neutralFocus: '#083344',      // cyan-950
    
    base100: '#f0fdfa',           // teal-50
    base200: '#ccfbf1',           // teal-100
    base300: '#99f6e4',           // teal-200
    baseContent: '#134e4a',       // teal-900
    
    info: '#0ea5e9',
    infoContent: '#ffffff',
    success: '#10b981',
    successContent: '#ffffff',
    warning: '#f59e0b',
    warningContent: '#ffffff',
    error: '#ef4444',
    errorContent: '#ffffff',
  },
  borderRadius: {
    base: '0.75rem',
    box: '1.25rem',
    btn: '0.75rem',
    badge: '9999px',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: true,
    animated: true,
  },
};

/**
 * Tema Forest (Naturale)
 */
const forest: Theme = {
  name: 'forest',
  displayName: '🌲 Forest',
  colors: {
    primary: '#10b981',           // green-500
    primaryContent: '#ffffff',
    primaryFocus: '#059669',      // green-600
    
    secondary: '#84cc16',         // lime-500
    secondaryContent: '#1a2e05',
    secondaryFocus: '#65a30d',    // lime-600
    
    accent: '#eab308',            // yellow-500
    accentContent: '#1a2e05',
    accentFocus: '#ca8a04',       // yellow-600
    
    neutral: '#1e3a20',
    neutralContent: '#f7fee7',
    neutralFocus: '#132815',
    
    base100: '#f7fee7',           // lime-50
    base200: '#ecfccb',           // lime-100
    base300: '#d9f99d',           // lime-200
    baseContent: '#1a2e05',       // lime-950
    
    info: '#06b6d4',
    infoContent: '#ffffff',
    success: '#22c55e',           // green-500
    successContent: '#ffffff',
    warning: '#f59e0b',
    warningContent: '#ffffff',
    error: '#ef4444',
    errorContent: '#ffffff',
  },
  borderRadius: {
    base: '0.5rem',
    box: '1rem',
    btn: '0.5rem',
    badge: '9999px',
  },
  fontFamily: {
    base: 'system-ui, -apple-system, sans-serif',
    heading: 'system-ui, -apple-system, sans-serif',
  },
  parameters: {
    rounded: true,
    animated: true,
  },
};

/**
 * Mappa di tutti i temi disponibili
 */
export const themes: Record<ThemeName, Theme> = {
  light,
  dark,
  cupcake,
  corporate,
  synthwave,
  retro,
  cyberpunk,
  valentine,
  aqua,
  forest,
};

/**
 * Lista nomi temi
 */
export const themeNames: ThemeName[] = Object.keys(themes) as ThemeName[];

/**
 * Tema di default
 */
export const defaultTheme: ThemeName = 'light';

/**
 * Helper per ottenere un tema
 */
export function getTheme(name: ThemeName): Theme {
  return themes[name] || themes[defaultTheme];
}

